const NON_REPORTING_HOURS = 30;

// API timestamps are UTC but lack timezone suffix - ensure parsed as UTC
const parseUtcTimestamp = (ts) => {
    if (!ts) return NaN;
    let s = String(ts);
    if (!s.endsWith('Z') && !s.includes('+') && !s.includes('T')) {
        s = s.replace(' ', 'T') + 'Z';
    }
    return new Date(s).getTime();
};

// A device that has never sent a packet. The API withholds its coordinates, so it
// cannot be mapped, and it should read as pending rather than offline.
export const isNeverReported = (device) => device?.never_reported === true;

// A just-activated device whose position is the approximate activation seed, not a
// real GPS fix. The API flags it (and returns its lat/lng) only when the request
// asked with ?approximate=1. A raw Postgres boolean can arrive as 't'/1, so read
// loosely. The map draws it faded until the first real fix.
export const isApproximate = (device) => {
    const v = device?.approximate;
    return v === true || v === 1 || v === '1' || v === 't' || v === 'true';
};

// Placeholder coordinates that are not a real position: the old (34,-84) and the
// current activation default (central US, mirrors activationLocation.js DEFAULT_*
// and the API's services.default_location). A device sitting on one gets no marker,
// so an approximate seed only ever shows at a meaningful company/user location.
export const isDefaultCoord = (lat, lng) => {
    const a = Number(lat);
    const b = Number(lng);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    if (Math.abs(a - 34.0) < 0.001 && Math.abs(b + 84.0) < 0.001) return true;
    return Math.abs(a - 38.736040) < 0.0005 && Math.abs(b + 100.274368) < 0.0005;
};

// Map on coordinates alone. The API withholds them while never_reported is true
// (unless ?approximate=1 opted into the seed), and MQTT can deliver a first fix
// before the next poll clears that flag, so the device must still map once it has
// real coordinates. A placeholder/default coordinate is not a real position.
export const hasMappableCoords = (device) =>
    device?.lat != null && device?.lng != null && !isDefaultCoord(device.lat, device.lng);

// Per-asset "Show on map" preference. The device keeps tracking and stays in the
// list, so this gates only the marker and the fit-to-bounds.
export const isHiddenOnMap = (device) => device?.hidden_on_map === true;

export const isShownOnMap = (device) => hasMappableCoords(device) && !isHiddenOnMap(device);

export const isNonReporting = (device) => {
    if (device.fleet_status) return device.fleet_status === 'offline';
    if (!device.last_message_received) return true;
    const hoursDiff = (Date.now() - parseUtcTimestamp(device.last_message_received)) / (1000 * 60 * 60);
    return hoursDiff > NON_REPORTING_HOURS;
};

export const isRecentlyReported = (device) => {
    if (!device.last_message_received) return false;
    const minsDiff = (Date.now() - parseUtcTimestamp(device.last_message_received)) / (1000 * 60);
    return minsDiff <= 3;
};

export const isIdle = (device) => {
    if (device.fleet_status) return device.fleet_status === 'idling';
    return device.motion_state === 1 && (device.gps_speed || 0) <= 2 && isRecentlyReported(device);
};
export const isMoving = (device) => {
    if (device.fleet_status) return device.fleet_status === 'moving';
    return device.motion_state === 1 && !isIdle(device) && !isNonReporting(device);
};
export const isStationary = (device) => {
    if (device.fleet_status) return device.fleet_status === 'parked';
    return !isMoving(device) && !isIdle(device) && !isNonReporting(device);
};
export const isLowBattery = (device) => device.battery_bars != null && device.battery_bars < 2;

// Fuel tank percent from a live device row (only OBD vehicles ever carry it),
// or null when there is no reading. The field is already 0-100 from
// DeviceLiveViewResource; a sentinel / out-of-range value reads as no reading.
export const fuelPercent = (device) => {
    const raw = device?.fuel_level ?? device?.device_status?.fuel_level;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
};

// Fuel at or below this percent is "low": matches the server low-fuel alert
// (fires <20%, re-arms >25%) and the fleet/Go apps (kFuelLowAlertThreshold = 20).
export const FUEL_LOW_THRESHOLD = 20;
export const isLowFuel = (device) => {
    const p = fuelPercent(device);
    return p !== null && p < FUEL_LOW_THRESHOLD;
};

// "Moving" filter includes both moving and idling (all active devices)
export const isMovingOrIdle = (device) => isMoving(device) || isIdle(device);

export const fleetFilterFns = {
    total: () => true,
    moving: isMovingOrIdle,
    idle: isIdle,
    stationary: isStationary,
    non_reporting: isNonReporting,
    low_battery: isLowBattery,
};

export const serverFilterKeys = new Set(['check_engine', 'pending_inspection', 'pending_maintenance', 'no_driver']);

export const isServerFilter = (key) => serverFilterKeys.has(key);

/**
 * Returns "Parked" for vehicle/equipment categories, "Stationary" for others.
 * Accepts a device object with `asset_type_category`.
 */
export const getStoppedLabel = (device) => {
    const cat = (device?.asset_type_category || '').toLowerCase();
    return (cat === 'vehicle' || cat === 'equipment') ? 'Parked' : 'Stationary';
};

export const fleetFilterLabels = {
    total: 'Total',
    moving: 'Moving',
    idle: 'Idling',
    stationary: 'Stationary',
    non_reporting: 'Offline',
    low_battery: 'Low Battery',
    check_engine: 'Check Engine',
    pending_inspection: 'Inspection Due',
    pending_maintenance: 'Service Due',
    no_driver: 'No Driver',
};

export const filterDevicesByFleetStatus = (devices, filterKey, serverAssetIds) => {
    if (!filterKey || filterKey === 'total') return devices || [];

    // Server-side filters: filter by asset_id membership
    if (isServerFilter(filterKey) && serverAssetIds) {
        const idSet = serverAssetIds[filterKey];
        if (!idSet || idSet.size === 0) return [];
        return (devices || []).filter(d => idSet.has(d.asset_id));
    }

    const fn = fleetFilterFns[filterKey];
    return fn ? (devices || []).filter(fn) : (devices || []);
};
